import { useEffect } from "react";
import useUpdateEffect from "react-use/lib/useUpdateEffect";
import * as R from "remeda";
import { useStore } from "zustand";

import { dimensionHydrationStore } from "../../store/dimension.hydration";
import {
    DimensionStatus,
    dimensionStatusStore,
} from "../../store/dimension.status";
import type {
    BaseDimensionValue,
    ListBuilderDimensionValue,
    MultiSelectDimensionValue,
} from "../../store/dimension.value";
import { dimensionValueStore } from "../../store/dimension.value";
import type {
    InputParameterFilter,
    RequestParameters,
    Filter,
} from "../../types/common";
import { Comparator } from "../../types/common";
import { DimensionTypes } from "../../types/dimensions";
import { buildQueryParameterFilter } from "../../utils/app.utils";

/**
 * Given a list of dimensions check if all statuses are allValid
 *
 * @param dimensions: list of dimensions
 */
export function useCheckDimensionStatus(dimensions: string[]): boolean {
    const dimensionStatuses: DimensionStatus[] = useStore(
        dimensionStatusStore,
        (state) => dimensions.map((d) => state[d]),
    ).filter(R.isDefined);

    if (dimensionStatuses.length !== dimensions.length) {
        return false;
    }
    const allValid = !dimensionStatuses.some(
        (d) => d !== DimensionStatus.SUCCESS,
    );
    return allValid;
}

export function useIsAnyDimensionIdLoading(dimensions: string[]): boolean {
    const dimensionStatuses: DimensionStatus[] = useStore(
        dimensionStatusStore,
        (state) => dimensions.map((d) => state[d]),
    ).map((d) => (R.isDefined(d) ? d : DimensionStatus.ERROR));
    if (dimensionStatuses.length !== dimensions.length) {
        return false;
    }
    return dimensionStatuses.some((d) => d === DimensionStatus.LOADING);
}

/**
 * Merges all request parameter objects into one
 *
 * @param dimensions - list of dimensions to merge
 */
export function useGetRequestParameters(
    dimensions: string[],
): RequestParameters {
    const values = useStore(dimensionValueStore, (state) => [
        ...dimensions.map((d) => state[d]),
    ]);
    const requestParameters = R.pipe(
        values,
        R.filter(R.isDefined),
        R.map((e) => e.requestParameters),
        R.mergeAll,
    ) as RequestParameters;
    return requestParameters;
}

/**
 * Provides function to set dimension value
 *
 * @param dimension - name of dimension
 * @param processRequestParameter - creates a request parameters for dimension
 */
export function useSetDimensionValue<T extends BaseDimensionValue>(
    dimension: string,
    processRequestParameter: (data: T) => RequestParameters,
): [T, (data: T) => void] {
    const [dimensionData, setData] = useStore(dimensionValueStore, (state) => [
        state.getDimensionData(dimension),
        state.setData,
    ]);
    const setDimensionData = (data: T) => {
        setData(dimension, data, processRequestParameter(data));
    };
    return [dimensionData as T, setDimensionData];
}

/**
 * Computes if dimension needs to be disabled
 *
 * @param dependencies - list of dependent dimensions
 */
export function useGetDimensionDisable(dependencies: string[]): boolean {
    const allValid = useCheckDimensionStatus(dependencies);
    return !allValid;
}

/**
 * Sets up a useEffect hook which listens on a dimension value
 * When value changes, new dimension status is computed
 * @typedef T - should be a dimension value type that extends base dimension value
 * @param dimension - name of dimension
 * @param processDimensionStatus - function that computes and returns DimensionStatus
 */
export function useUpdateStatusOnChange<T extends BaseDimensionValue>(
    dimension: string,
    processDimensionStatus: (v: T) => DimensionStatus,
): void {
    const [dimensionData] = useStore(dimensionValueStore, (state) => [
        state[dimension],
    ]);

    const [setStatus, dimensionStatus] = useStore(
        dimensionStatusStore,
        (state) => [state.setStatus, state[dimension]],
    );

    useEffect(() => {
        if (
            dimensionStatus !== DimensionStatus.PENDING_HYDRATION &&
            dimensionData
        ) {
            setStatus(
                dimension,
                processDimensionStatus(dimensionData?.data as any),
            ); // union typing issue :/
        }
    }, [dimensionData || {}]);
}

/**
 * Given a list of dependencies, when depencency values change clear current value
 * @param dimension - name of dimension
 * @param dependencies - list of dependant dimensions
 */
export function useClearDimensionValueOnDependencyChange(
    dimension: string,
    dependencies: string[],
): void {
    const values = useStore(dimensionValueStore, (state) =>
        dependencies.map((e) => state[e]?.data.value),
    );
    const setData = useStore(dimensionValueStore, (state) => state.setData);
    const [setStatus, dimensionStatus] = useStore(
        dimensionStatusStore,
        (state) => [state.setStatus, state[dimension]],
    );

    useUpdateEffect(() => {
        if (
            !(
                dimensionStatus === DimensionStatus.LOADING ||
                dimensionStatus === DimensionStatus.PENDING_HYDRATION
            )
        ) {
            setStatus(dimension, DimensionStatus.EMPTY);
            setData(dimension, { value: "" }, {});
        }
    }, values);
}

/**
 * Given a list of parameter filters fetch dimension values and create filter list
 */
export function buildQueryParamFiltersFromDimensions(
    filters: InputParameterFilter[],
): Filter[] {
    return R.pipe(
        filters,
        R.map((e) => ({
            ...e,
            value: getValueFromDimensionTyped(e.dimension ?? "", e.condition),
        })),
        R.filter((e) => R.isTruthy(e.value)),
        R.map((e) =>
            buildQueryParameterFilter(
                e.key,
                e.condition,
                e.value as any,
                e.type,
            ),
        ),
    );
}

export function getValueFromDimensionTyped(
    dimension: string,
    comparator: Comparator,
): string | string[] {
    const dimensionValue = dimensionValueStore.getState()[dimension];
    if (dimensionValue) {
        if (dimensionValue.data.type === DimensionTypes.MULTISELECT) {
            if (comparator !== Comparator.IN) {
                throw new Error(
                    "Multiselect value must be paired with the IN comparator",
                );
            }
            return (dimensionValue.data as MultiSelectDimensionValue)
                .selectedItems;
        } else if (dimensionValue.data.type === DimensionTypes.LIST_BUILDER) {
            if (comparator !== Comparator.IN) {
                throw new Error(
                    "Multiselect value must be paired with the IN comparator",
                );
            }
            return (dimensionValue.data as ListBuilderDimensionValue)
                .selectedItems;
        } else {
            return dimensionValue.data.value;
        }
    } else {
        return "";
    }
}

export function useClearAndInitDimensionStores(
    dimensions: string[],
    hydrationPayload?: object,
): void {
    useEffect(() => {
        dimensionValueStore.getState().clearAllValues();
        dimensionStatusStore.getState().clearStatus();
        dimensionHydrationStore.getState().clearAllState();
        if (hydrationPayload) {
            dimensionStatusStore
                .getState()
                .initDimensions(dimensions, DimensionStatus.PENDING_HYDRATION);
            dimensionHydrationStore
                .getState()
                .initHydration(dimensions, hydrationPayload);
        } else {
            dimensionStatusStore.getState().initDimensions(dimensions);
        }
        dimensionValueStore.getState().initDimensions(dimensions);
    }, dimensions);
}
